<script setup lang="ts" generic="T">
import type { EmblaCarouselType, EmblaOptionsType, EmblaPluginType } from "embla-carousel";
import emblaCarouselVue from "embla-carousel-vue";

type Breakpoint = "base" | "sm" | "md" | "lg" | "xl";
type Cols = 1 | 2 | 3;

const COLS_CLASS: Record<Breakpoint, Record<Cols, string>> = {
  base: {
    1: "[--carousel-slide-size:100%]",
    2: "[--carousel-slide-size:calc((100%-1*var(--carousel-gap,1rem))/2)]",
    3: "[--carousel-slide-size:calc((100%-2*var(--carousel-gap,1rem))/3)]",
  },
  sm: {
    1: "sm:[--carousel-slide-size:100%]",
    2: "sm:[--carousel-slide-size:calc((100%-1*var(--carousel-gap,1rem))/2)]",
    3: "sm:[--carousel-slide-size:calc((100%-2*var(--carousel-gap,1rem))/3)]",
  },
  md: {
    1: "md:[--carousel-slide-size:100%]",
    2: "md:[--carousel-slide-size:calc((100%-1*var(--carousel-gap,1rem))/2)]",
    3: "md:[--carousel-slide-size:calc((100%-2*var(--carousel-gap,1rem))/3)]",
  },
  lg: {
    1: "lg:[--carousel-slide-size:100%]",
    2: "lg:[--carousel-slide-size:calc((100%-1*var(--carousel-gap,1rem))/2)]",
    3: "lg:[--carousel-slide-size:calc((100%-2*var(--carousel-gap,1rem))/3)]",
  },
  xl: {
    1: "xl:[--carousel-slide-size:100%]",
    2: "xl:[--carousel-slide-size:calc((100%-1*var(--carousel-gap,1rem))/2)]",
    3: "xl:[--carousel-slide-size:calc((100%-2*var(--carousel-gap,1rem))/3)]",
  },
};

const OVERFLOW_VISIBLE_CLASS: Record<string, string> = {
  sm: "sm:overflow-visible",
  md: "md:overflow-visible",
  lg: "lg:overflow-visible",
  xl: "xl:overflow-visible",
};

const props = withDefaults(
  defineProps<{
    items: T[];
    itemKey?: (item: T, index: number) => string | number;
    options?: EmblaOptionsType;
    plugins?: EmblaPluginType[];
    controls?: boolean;
    previousLabel?: string;
    nextLabel?: string;
    label?: string;
    slideLabel?: (index: number, total: number) => string;
    cols?: Cols | Partial<Record<Breakpoint, Cols>>;
    ratio?: number;
    overflowVisible?: boolean | "sm" | "md" | "lg" | "xl";
  }>(),
  {
    controls: true,
    overflowVisible: false,
  }
);

const ratioStyle = computed(() => (props.ratio !== undefined ? { "--carousel-ratio": String(props.ratio) } : null));

const overflowVisibleClass = computed(() => {
  if (props.overflowVisible === true) return "overflow-visible";
  if (typeof props.overflowVisible === "string") return OVERFLOW_VISIBLE_CLASS[props.overflowVisible] ?? "";
  return "";
});

const colsClasses = computed(() => {
  const cols = props.cols;
  if (cols === undefined) return "";
  if (typeof cols === "number") return COLS_CLASS.base[cols] ?? "";
  return Object.entries(cols)
    .map(([bp, n]) => COLS_CLASS[bp as Breakpoint]?.[n as Cols] ?? "")
    .filter(Boolean)
    .join(" ");
});

const { t } = useI18n();

const carouselOptions = computed<EmblaOptionsType>(() => ({
  align: "start",
  containScroll: "trimSnaps",
  duration: 15,
  watchDrag: (api) => api.scrollSnapList().length > 1,
  ...props.options,
}));

const carouselPlugins = computed(() => props.plugins ?? []);

const [emblaRef, emblaApi] = emblaCarouselVue(carouselOptions, carouselPlugins);

watch(
  () => props.items.length,
  () => emblaApi.value?.reInit()
);

const canScrollPrev = ref(false);
const canScrollNext = ref(false);
const selectedIndex = ref(0);
const scrollSnaps = ref<number[]>([]);
const isScrollable = computed(() => scrollSnaps.value.length > 1);

function syncState(api: EmblaCarouselType) {
  canScrollPrev.value = api.canScrollPrev();
  canScrollNext.value = api.canScrollNext();
  selectedIndex.value = api.selectedScrollSnap();
  scrollSnaps.value = api.scrollSnapList();
}

watch(
  emblaApi,
  (api) => {
    if (!api) return;
    syncState(api);
    api.on("select", syncState);
    api.on("reInit", syncState);
  },
  { immediate: true }
);

function scrollPrev() {
  emblaApi.value?.scrollPrev();
}
function scrollNext() {
  emblaApi.value?.scrollNext();
}
function scrollTo(index: number) {
  emblaApi.value?.scrollTo(index);
}
function reInit() {
  emblaApi.value?.reInit();
}

defineExpose({
  api: emblaApi,
  selectedIndex,
  scrollSnaps,
  canScrollPrev,
  canScrollNext,
  isScrollable,
  scrollPrev,
  scrollNext,
  scrollTo,
  reInit,
});

defineSlots<{
  default(props: { item: T; index: number }): unknown;
  footer(props: {
    selectedIndex: number;
    scrollSnaps: number[];
    canScrollPrev: boolean;
    canScrollNext: boolean;
    isScrollable: boolean;
    scrollPrev: () => void;
    scrollNext: () => void;
    scrollTo: (index: number) => void;
  }): unknown;
  baseline(props: { selectedIndex: number; scrollSnaps: number[] }): unknown;
}>();
</script>

<template>
  <div :style="ratioStyle" :class="colsClasses" class="flex flex-col gap-(--carousel-vertical-gap,--spacing(3))">
    <div class="relative aspect-(--carousel-ratio)">
      <div
        ref="emblaRef"
        class="size-full overflow-hidden"
        :class="[overflowVisibleClass, { 'absolute size-full': !!ratio }]"
        role="region"
        aria-roledescription="carousel"
        :aria-label="label"
      >
        <ul class="h-full flex">
          <li
            v-for="(item, index) in items"
            :key="itemKey ? itemKey(item, index) : index"
            class="select-none h-full basis-(--carousel-slide-size,100%) min-w-0 shrink-0 mr-(--carousel-gap,1rem) last:mr-0"
            role="group"
            aria-roledescription="slide"
            :aria-label="slideLabel ? slideLabel(index, items.length) : t('carousel.slide', { current: index + 1, total: items.length })"
          >
            <slot :item="item" :index="index" />
          </li>
        </ul>
      </div>
    </div>
    <slot
      name="footer"
      :selected-index="selectedIndex"
      :scroll-snaps="scrollSnaps"
      :can-scroll-prev="canScrollPrev"
      :can-scroll-next="canScrollNext"
      :is-scrollable="isScrollable"
      :scroll-prev="scrollPrev"
      :scroll-next="scrollNext"
      :scroll-to="scrollTo"
    >
      <div v-if="(controls && isScrollable) || $slots.baseline" class="flex items-start justify-between gap-4">
        <div v-if="$slots.baseline" class="grow">
          <slot name="baseline" :selected-index="selectedIndex" :scroll-snaps="scrollSnaps" />
        </div>
        <div v-if="controls && isScrollable" class="ml-auto flex justify-end shrink-0 gap-1">
          <AppCarouselButton
            direction="prev"
            :disabled="!canScrollPrev"
            :aria-label="previousLabel ?? t('ui.previous')"
            @click="scrollPrev"
          />
          <AppCarouselButton direction="next" :disabled="!canScrollNext" :aria-label="nextLabel ?? t('ui.next')" @click="scrollNext" />
        </div>
      </div>
    </slot>
  </div>
</template>
